import React, { useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { HiLockClosed, HiOutlineMail, HiOutlineLockClosed } from 'react-icons/hi';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';

import TextInput from '../../components/inputs/Text';
import Button from '../../components/inputs/Button';
import PageLoader from '../../components/common/PageLoader';

// eslint-disable-next-line import/no-unresolved
import PurpleLogo from '../../assets/images/purple-logo.png.png';
// eslint-disable-next-line import/no-unresolved
import CityBg from '../../assets/images/cityscape.png.png';
import { useAuthenticateMutation } from '../../services';
import signInValidation from '../../validations/sign-in';
import {
  loginSuccess, setEmail, setRole, useAppDispatch, useAppSelector
} from '../../store';
import {
  decodeJwt, getCookie, getErrorMessage, setCookie
} from '../../utilities';
import { ACCESS_TOKEN_AGE, REFRESH_TOKEN_AGE } from '../../constants';

function Index() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { loggedIn } = useAppSelector((state) => state?.auth);

  const returnTo = location.state?.from || '/dashboard';

  const [proceedToLogin, { isLoading }] = useAuthenticateMutation();

  useEffect(() => {
    if (loggedIn || getCookie('_tk')) {
      navigate(returnTo, { replace: true });
    }
  }, [loggedIn, navigate, returnTo]);

  const initialValues = {
    emailAddress: '',
    password: ''
  };

  const signInFormik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: signInValidation,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);

        const response = await proceedToLogin({
          emailAddress: values.emailAddress,
          password: values.password
        }).unwrap();

        const token = response?.loginToken;

        if (!token) {
          toast.error('Invalid response from server. Please try again.');
          return;
        }

        setCookie('_tk', token, ACCESS_TOKEN_AGE);

        if (response?.refreshToken) {
          setCookie('_rtk', response.refreshToken, REFRESH_TOKEN_AGE);
          const rtkExp = Math.floor(Date.now() / 1000) + REFRESH_TOKEN_AGE;
          setCookie('_rtk_exp', String(rtkExp), REFRESH_TOKEN_AGE);
        }

        const decodedUser = decodeJwt(token);

        if (!decodedUser) {
          toast.error('Failed to process authentication. Please try again.');
          return;
        }

        dispatch(loginSuccess(decodedUser));
        dispatch(setEmail(decodedUser.Email));
        if (decodedUser.aud) {
          dispatch(setRole(decodedUser.aud));
        }

        toast.success('Sign in successful');
        navigate(returnTo, { replace: true });
      } catch (error) {
        const message = getErrorMessage(error);
        toast.error(message);
      } finally {
        setSubmitting(false);
      }
    }
  });

  return (
    <>
      {(signInFormik.isSubmitting || isLoading) && <PageLoader isTransparent />}

      <section
        className="relative min-h-screen flex items-center justify-center px-4 py-10 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${CityBg})` }}
      >
        <div className="absolute top-6 left-6">
          <Link to="/">
            <img src={PurpleLogo} alt="Purple Academy" className="h-10 object-contain" />
          </Link>
        </div>
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl px-10 py-10">

          <div className="flex justify-center mb-5">
            <Link to="/">
              <img src={PurpleLogo} alt="Purple Academy" className="h-10 object-contain" />
            </Link>
          </div>

          <div className="flex justify-center mb-4">
            <HiLockClosed className="text-3xl text-gray-800" />
          </div>

          <h1 className="text-lg font-bold text-center text-gray-800 mb-6">
            Sign in to your account
          </h1>

          <form onSubmit={signInFormik.handleSubmit}>
            <div className="relative mb-4">
              <HiOutlineMail className="absolute left-3 top-[38px] text-gray-400 text-lg pointer-events-none z-10" />
              <TextInput
                label="Email Address"
                labelClassName="!text-gray-600 !text-sm !font-medium"
                placeholder="Name@wemabank.com"
                type="email"
                name="emailAddress"
                inputClassName="!bg-white !text-gray-800 !border-gray-200 !rounded-xl !font-normal pl-9 placeholder:!text-gray-400"
                error={signInFormik.errors.emailAddress}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...signInFormik.getFieldProps('emailAddress')}
              />
            </div>

            <div className="relative mb-2">
              <HiOutlineLockClosed className="absolute left-3 top-[38px] text-gray-400 text-lg pointer-events-none z-10" />
              <TextInput
                label="Password"
                labelClassName="!text-gray-600 !text-sm !font-medium"
                placeholder="••••••••••"
                type="password"
                name="password"
                inputClassName="!bg-white !text-gray-800 !border-gray-200 !rounded-xl !font-normal pl-9 placeholder:!text-gray-400"
                error={signInFormik.errors.password}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...signInFormik.getFieldProps('password')}
              />
            </div>

            <div className="flex justify-end mb-6">
              <button type="button" className="text-sm text-primary hover:underline font-medium">
                Forgot Password?
              </button>
            </div>

            <Button
              type="submit"
              bgColor="bg-primary"
              textColor="text-white"
              paddingY="py-3"
              className="w-full text-base font-semibold rounded-xl"
            >
              Sign in
            </Button>
          </form>

          <div className="flex justify-center mt-5">
            <button type="button" className="text-sm text-primary hover:underline font-medium">
              Contact Support
            </button>
          </div>
        </div>
      </section>
    </>
  );
}

export default Index;
